import React, { useState, useEffect } from "react";
import { Property, Kiosk } from "@/types";
import FirebaseService from "@/utils/firebaseService";
import { Spinner } from "@/components/atomic";

interface PropertyKioskSelectionModalProps {
  isOpen: boolean;
  onSelect: (property: Property, kiosk: Kiosk) => void;
  onClose: () => void;
}

const PropertyKioskSelectionModal: React.FC<
  PropertyKioskSelectionModalProps
> = ({ isOpen, onSelect, onClose }) => {
  const [properties, setProperties] = useState<Property[]>([]);
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>("");
  const [selectedKioskId, setSelectedKioskId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const firebaseService = FirebaseService.getInstance();

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [propertiesData, kiosksData] = await Promise.all([
        firebaseService.getProperties(),
        firebaseService.getKiosks(),
      ]);

      setProperties(propertiesData);
      setKiosks(kiosksData);

      // Auto-select first property and its first kiosk if available
      if (propertiesData.length > 0) {
        const firstProperty = propertiesData[0];
        setSelectedPropertyId(firstProperty.id);

        const firstKiosk = kiosksData.find(
          (k) => k.propertyId === firstProperty.id
        );
        if (firstKiosk) {
          setSelectedKioskId(firstKiosk.id);
        }
      }
    } catch (err) {
      console.error("❌ Error loading data:", err);
      // Show more specific error message to help debug
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Failed to load properties and kiosks. Please try again.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePropertyChange = (propertyId: string) => {
    setSelectedPropertyId(propertyId);
    setSelectedKioskId(""); // Reset kiosk selection

    // Auto-select first kiosk of the selected property
    const propertyKiosks = kiosks.filter((k) => k.propertyId === propertyId);
    if (propertyKiosks.length > 0) {
      setSelectedKioskId(propertyKiosks[0].id);
    }
  };

  const handleConfirm = () => {
    const selectedProperty = properties.find(
      (p) => p.id === selectedPropertyId
    );
    const selectedKiosk = kiosks.find((k) => k.id === selectedKioskId);

    if (selectedProperty && selectedKiosk) {
      onSelect(selectedProperty, selectedKiosk);
    }
  };

  const filteredKiosks = kiosks.filter(
    (k) => k.propertyId === selectedPropertyId
  );
  const selectedProperty = properties.find((p) => p.id === selectedPropertyId);
  const selectedKiosk = kiosks.find((k) => k.id === selectedKioskId);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Select Property & Kiosk</h2>
          <button className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading-container">
              <Spinner size="large" />
              <p>Loading properties and kiosks...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <p className="error-message">{error}</p>
              <button onClick={loadData} className="retry-btn">
                Retry
              </button>
            </div>
          ) : (
            <>
              <div className="form-group">
                <label htmlFor="property-select">Property:</label>
                <select
                  id="property-select"
                  value={selectedPropertyId}
                  onChange={(e) => handlePropertyChange(e.target.value)}
                  className="form-select"
                >
                  <option value="">Select a property</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name} - {property.address.city},{" "}
                      {property.address.province}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="kiosk-select">Kiosk:</label>
                <select
                  id="kiosk-select"
                  value={selectedKioskId}
                  onChange={(e) => setSelectedKioskId(e.target.value)}
                  className="form-select"
                  disabled={!selectedPropertyId}
                >
                  <option value="">Select a kiosk</option>
                  {filteredKiosks.map((kiosk) => (
                    <option key={kiosk.id} value={kiosk.id}>
                      {kiosk.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProperty && selectedKiosk && (
                <div className="selection-summary">
                  <h3>Selected:</h3>
                  <p>
                    <strong>Property:</strong> {selectedProperty.name}
                  </p>
                  <p>
                    <strong>Address:</strong>{" "}
                    {selectedProperty.address.address1},{" "}
                    {selectedProperty.address.city},{" "}
                    {selectedProperty.address.province}, {selectedProperty.address.zip}, {selectedProperty.address.country}, {selectedProperty.address.phone}
                  </p>
                  <p>
                    <strong>Kiosk:</strong> {selectedKiosk.name}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button
            onClick={handleConfirm}
            disabled={!selectedPropertyId || !selectedKioskId || isLoading}
            className="confirm-btn"
          >
            Confirm Selection
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyKioskSelectionModal;
